/**
 * @fileoverview Clinical History Modal Component
 * @module components/modals/ClinicalHistoryModal
 * @description A modal component for displaying the clinical note history for a member.
 * Draft notes can be reopened; submitted notes open in read-only mode.
 */

import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  CircularProgress,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import ScienceIcon from "@mui/icons-material/Science";
import PropTypes from "prop-types";
import { exportClinicalAssessmentPdfFromHistory } from "../../utils/clinicalAssessmentPdf";

import PAPIcon from "../../assets/PAP.svg";
import InternationalSourcingIcon from "../../assets/IntSourcing.svg";
import CopayAssistIcon from "../../assets/CoPayAssist.svg";
import ECMPIcon from "../../assets/ECMP.svg";
import GLP1Icon from "../../assets/GLP 1.svg";
import BiosimilarsIcon from "../../assets/Biosimilars.svg";
import WomensHealthIcon from "../../assets/WomensHealth.svg";
import DiabetesIcon from "../../assets/Diabetes.svg";
import WeightManagementIcon from "../../assets/WeightManagement.svg";

import { getClinicalNoteHistory } from "../../api/assessment";
import { formatTimeOnly } from "../../utils/formatDateTime";
import { capitalizeFirstLetter } from "../../utils/capitalize";
import { useLoading } from "../../context/LoadingContext";

const formatDateMMDDYYYY = (dateString) => {
  if (!dateString) return "";
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const fixed = String(dateString).endsWith("Z")
      ? String(dateString)
      : String(dateString) + "Z";
    const date = new Date(fixed);
    if (Number.isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      timeZone: tz,
    });
  } catch (err) {
    console.error("Error formatting date:", err);
    return dateString;
  }
};

const calculateDuration = (start, end) => {
  if (!start || !end) return "—";
  try {
    const fixedStart = String(start).endsWith("Z")
      ? String(start)
      : String(start) + "Z";
    const fixedEnd = String(end).endsWith("Z")
      ? String(end)
      : String(end) + "Z";
    const startDate = new Date(fixedStart);
    const endDate = new Date(fixedEnd);
    const diffMs = endDate - startDate;
    if (Number.isNaN(diffMs) || diffMs < 0) return "—";

    const totalSeconds = Math.floor(diffMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    const pad = (num) => String(num).padStart(2, "0");
    return `${pad(hours)}:${pad(minutes)}:${pad(seconds)}`;
  } catch (e) {
    console.debug("Duration calculation failed:", e);
    return "—";
  }
};

// Build a per-row merge plan. Consecutive records that share the same
// startTime & completionTime are grouped so their merged columns
// (Assessment Type, Start Date, Completion Date, Time Spent, Submitted By)
// can be displayed in a single vertically-merged cell (rowSpan). The
// Program(s), Program Outcome and Action columns always render per row.
// The Id column displays IDs in descending order.
const buildMergePlan = (history) => {
  if (!history || history.length === 0) return [];

  const labelMap = {
    INITIAL: "Initial Assessment",
    FOLLOW_UP: "Follow-up Assessment",
    DISCHARGE: "Discharge Assessment",
    OTHER: "Other",
  };

  const keyOf = (item) => {
    if (item.batchId) return `batch_${item.batchId}`;
    return `${item.startTime || ""}|${item.completionTime || ""}`;
  };

  // Ensure all records sharing the same batchId (or session times) are contiguous
  const groupedHistory = [];
  const visitedKeys = new Set();
  history.forEach((item) => {
    const k = keyOf(item);
    if (!visitedKeys.has(k)) {
      visitedKeys.add(k);
      history.forEach((other) => {
        if (keyOf(other) === k) {
          groupedHistory.push(other);
        }
      });
    }
  });

  // Split contiguous items into distinct assessment session groups
  const groups = [];
  let i = 0;
  while (i < groupedHistory.length) {
    const key = keyOf(groupedHistory[i]);
    let j = i + 1;
    while (j < groupedHistory.length && keyOf(groupedHistory[j]) === key) {
      j += 1;
    }
    groups.push(groupedHistory.slice(i, j));
    i = j;
  }

  const totalGroups = groups.length;
  const plan = [];

  groups.forEach((groupItems, groupIndex) => {
    const groupSize = groupItems.length;
    // Descending ID for the assessment session (e.g. totalGroups down to 1)
    const currentId = totalGroups - groupIndex;

    if (groupSize > 1) {
      // Combined Assessment Type labels
      const typeLabels = [];
      groupItems.forEach((g) => {
        const t = g.assessmentType || g.type;
        if (
          t &&
          String(t).toUpperCase() !== "N/A" &&
          String(t).toUpperCase() !== "NA"
        ) {
          const label = labelMap[String(t).toUpperCase()] || t;
          if (!typeLabels.includes(label)) typeLabels.push(label);
        }
      });

      // Single Time Spent value - show one duration, do NOT sum merged records
      const singleDuration =
        groupItems[0].duration ||
        calculateDuration(
          groupItems[0].startTime,
          groupItems[0].completionTime,
        ) ||
        "—";

      // Submitted By - when all submitters are identical show the single value,
      // otherwise show the first submitter plus a count of the others.
      const submitters = groupItems
        .map((g) => g.submittedBy || g.submittedByName)
        .filter((s) => s && s !== "—");
      const uniqueSubmitters = [...new Set(submitters)];
      let submittedBy;
      if (uniqueSubmitters.length === 0) {
        submittedBy = "—";
      } else if (uniqueSubmitters.length === 1) {
        submittedBy = uniqueSubmitters[0];
      } else {
        submittedBy = `${uniqueSubmitters[0]} +${uniqueSubmitters.length - 1} others`;
      }

      const merged = {
        assessmentType: typeLabels.length > 0 ? typeLabels.join(", ") : "N/A",
        duration: singleDuration,
        submittedBy,
      };

      // First row of the group renders the spanned merged cells
      plan.push({
        item: groupItems[0],
        rowSpan: groupSize,
        emitMerged: true,
        merged,
        slNo: currentId,
        id: currentId,
      });
      // Following rows only render the per-row columns
      for (let k = 1; k < groupSize; k += 1) {
        plan.push({
          item: groupItems[k],
          rowSpan: groupSize,
          emitMerged: false,
          merged,
          slNo: currentId,
          id: currentId,
        });
      }
    } else {
      plan.push({
        item: groupItems[0],
        rowSpan: 1,
        emitMerged: true,
        merged: null,
        slNo: currentId,
        id: currentId,
      });
    }
  });

  return plan;
};
const getSvgFilter = (color) => {
  if (
    !color ||
    typeof color !== "string" ||
    !/^#([0-9A-F]{3}){1,2}$/i.test(color)
  ) {
    color = "#808080";
  }

  const hexToRgb = (hex) => {
    try {
      const hexValue =
        hex.length === 4
          ? "#" + hex[1] + hex[1] + hex[2] + hex[2] + hex[3] + hex[3]
          : hex;

      const r = Number.parseInt(hexValue.slice(1, 3), 16);
      const g = Number.parseInt(hexValue.slice(3, 5), 16);
      const b = Number.parseInt(hexValue.slice(5, 7), 16);
      return [r, g, b];
    } catch (e) {
      console.debug("Hex color parsing failed:", e);
      return [128, 128, 128];
    }
  };

  try {
    const [r, g, b] = hexToRgb(color);
    return `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='recolor' color-interpolation-filters='sRGB'%3E%3CfeColorMatrix type='matrix' values='0 0 0 0 ${
      r / 255
    } 0 0 0 0 ${g / 255} 0 0 0 0 ${
      b / 255
    } 0 0 0 1 0'/%3E%3C/filter%3E%3C/svg%3E#recolor")`;
  } catch (e) {
    console.debug("SVG filter generation failed:", e);
    return "none";
  }
};

const getCategoryIcon = (category) => {
  const iconStyle = {
    width: "16px",
    height: "16px",
    filter: getSvgFilter("#403C6F"),
  };

  const c = String(category || "").toLowerCase();

  if (c.includes("biosimilar")) {
    return <img src={BiosimilarsIcon} alt="Biosimilars" style={iconStyle} />;
  } else if (c.includes("pap")) {
    return <img src={PAPIcon} alt="PAP" style={iconStyle} />;
  } else if (c.includes("international") || c.includes("sourcing")) {
    return (
      <img
        src={InternationalSourcingIcon}
        alt="International Sourcing"
        style={iconStyle}
      />
    );
  } else if (c.includes("copay") || c.includes("assist")) {
    return <img src={CopayAssistIcon} alt="Copay Assist" style={iconStyle} />;
  } else if (c.includes("ecmp")) {
    return <img src={ECMPIcon} alt="ECMP" style={iconStyle} />;
  } else if (c.includes("glp") || c.includes("1")) {
    return <img src={GLP1Icon} alt="GLP-1" style={iconStyle} />;
  } else if (c.includes("women")) {
    return (
      <img src={WomensHealthIcon} alt="Women's Health" style={iconStyle} />
    );
  } else if (c.includes("diabete")) {
    return <img src={DiabetesIcon} alt="Diabetes" style={iconStyle} />;
  } else if (c.includes("weight")) {
    return (
      <img
        src={WeightManagementIcon}
        alt="Weight Management"
        style={iconStyle}
      />
    );
  }

  return (
    <ScienceIcon
      style={{
        width: "16px",
        height: "16px",
        color: "#403C6F",
      }}
    />
  );
};

const formatProgramName = (rawProgram) => {
  if (!rawProgram) return "General";
  return String(rawProgram)
    .split(/[\s_]+/)
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(" ");
};

const getProgramDisplayName = (item) => {
  const rawId =
    item.programId ||
    item.programCode ||
    (Array.isArray(item.programs)
      ? item.programs[0]?.programId || item.programs[0]
      : item.programName || item.program);
  return formatProgramName(rawId);
};

const OUTCOME_STYLES = {
  draft: {
    label: "Draft",
    backgroundColor: "#FFF8E7",
    color: "#B97B00",
    border: "1px solid #F5D87A",
  },
  enrolled: {
    label: "Enrolled",
    backgroundColor: "#EBF5F0",
    color: "#1C7A4B",
    border: "1px solid #7ECFAA",
  },
  completed_successfully: {
    label: "Completed",
    backgroundColor: "#EBF5F0",
    color: "#1C7A4B",
    border: "1px solid #7ECFAA",
  },
  declined: {
    label: "Declined",
    backgroundColor: "#FCE8E6",
    color: "#C53030",
    border: "1px solid #FEB2B2",
  },
  declined_participation: {
    label: "Declined",
    backgroundColor: "#FCE8E6",
    color: "#C53030",
    border: "1px solid #FEB2B2",
  },
  discharged: {
    label: "Discharged",
    backgroundColor: "#F7FAFC",
    color: "#4A5568",
    border: "1px solid #CBD5E0",
  },
  default: {
    backgroundColor: "#ECECF1",
    color: "#403C6F",
    border: "1px solid #C4C2D3",
  },
};

const getOutcomeStyle = (
  outcome = "",
  status = "",
  progObj = {},
  itemObj = {},
) => {
  const isDraft = String(status).toLowerCase() === "draft";
  if (isDraft) return OUTCOME_STYLES.draft;

  const key = String(outcome)
    .toLowerCase()
    .replace(/[\s/]+/g, "_");
  const style = { ...(OUTCOME_STYLES[key] || OUTCOME_STYLES.default) };

  const rawColor =
    progObj?.outcomeColour ||
    progObj?.outcomeColor ||
    progObj?.outcome_colour ||
    progObj?.outcome_color ||
    progObj?.outcomeColorCode ||
    progObj?.colorCode ||
    progObj?.color ||
    itemObj?.outcomeColour ||
    itemObj?.outcomeColor ||
    itemObj?.outcome_colour ||
    itemObj?.outcome_color ||
    itemObj?.outcomeColorCode ||
    itemObj?.colorCode ||
    itemObj?.color;

  if (rawColor) {
    const trimmed = String(rawColor).trim();
    style.color = /^[0-9A-Fa-f]{3,8}$/.test(trimmed) ? `#${trimmed}` : trimmed;
  }

  if (!style.label) {
    style.label = outcome
      ? outcome
          .split(/[\s_-]+/)
          .map(
            (word) =>
              word.charAt(0).toUpperCase() + word.slice(1).toLowerCase(),
          )
          .join(" ")
      : "—";
  }
  return style;
};

/**
 * ClinicalHistoryModal Component
 *
 * @param {Object}   props
 * @param {boolean}  props.open       - Controls modal visibility
 * @param {Function} props.onClose    - Callback when modal is closed
 * @param {string}   props.memberId   - ID of the member
 * @param {Object}   props.memberData - Member demographic data
 * @param {Function} props.onReopen   - Called with a draft note record so the caller can
 *                                      re-open the ClinicalNotePage pre-populated with that draft
 */
const getAssessmentTypeLabel = (item) => {
  const rawType = item.assessmentType || item.type || item.formType;
  if (
    !rawType ||
    String(rawType).toUpperCase() === "N/A" ||
    String(rawType).toUpperCase() === "NA"
  )
    return "N/A";
  const normType = String(rawType).toUpperCase();
  const labelMap = {
    INITIAL: "Initial Assessment",
    FOLLOW_UP: "Follow-up Assessment",
    DISCHARGE: "Discharge Assessment",
  };
  return labelMap[normType] || rawType;
};

const getTimeSpent = (entry, item) => {
  if (entry.merged) return entry.merged.duration;
  return (
    item.duration ||
    calculateDuration(item.startTime, item.completionTime) ||
    "—"
  );
};

const getSubmittedBy = (entry, item) => {
  if (entry.merged) return entry.merged.submittedBy;
  return item.submittedBy || item.submittedByName || "—";
};

const isDraftItem = (item) =>
  String(item.status || "").toLowerCase() === "draft";

const hasProgramList = (item) =>
  Array.isArray(item.programs) && item.programs.length > 0;

const renderDateTime = (value) =>
  value ? (
    <Box sx={{ display: "flex", flexDirection: "column" }}>
      <span>{formatDateMMDDYYYY(value)}</span>
      <span
        style={{
          color: "#66638C",
          fontSize: "12px",
          fontWeight: 400,
        }}
      >
        {formatTimeOnly(value)}
      </span>
    </Box>
  ) : (
    "—"
  );

const ClinicalHistoryModal = ({
  open,
  onClose,
  memberId,
  memberData,
  onReopen,
}) => {
  const [history, setHistory] = useState([]);
  const [error, setError] = useState(null);
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const { showLoading, hideLoading } = useLoading();

  const handleDownloadPdf = async (item) => {
    const id = item.batchId || item.id;
    const targetKey = item.batchId
      ? `batch_${item.batchId}`
      : `${item.startTime || ""}|${item.completionTime || ""}`;
    const allBatchItems = (history || []).filter((h) => {
      const k = h.batchId
        ? `batch_${h.batchId}`
        : `${h.startTime || ""}|${h.completionTime || ""}`;
      return k === targetKey;
    });
    try {
      setDownloadingPdfId(id);
      await exportClinicalAssessmentPdfFromHistory({
        item,
        allBatchItems: allBatchItems.length > 0 ? allBatchItems : [item],
        memberData: memberData || { memberId },
      });
    } catch (err) {
      console.error("Failed to export PDF:", err);
    } finally {
      setDownloadingPdfId(null);
    }
  };

  useEffect(() => {
    const run = async () => {
      try {
        showLoading();
        setError(null);
        const data = await getClinicalNoteHistory(memberId);
        const historyData = Array.isArray(data) ? [...data] : [];
        // Ensure assessments are sorted descending (latest first)
        historyData.sort((a, b) => {
          const timeA =
            new Date(
              a.startTime ||
                a.completionTime ||
                a.updatedAt ||
                a.createdAt ||
                0,
            ).getTime() || 0;
          const timeB =
            new Date(
              b.startTime ||
                b.completionTime ||
                b.updatedAt ||
                b.createdAt ||
                0,
            ).getTime() || 0;
          if (timeB !== timeA) return timeB - timeA;
          const idA = Number(a.id);
          const idB = Number(b.id);
          if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idB - idA;
          return 0;
        });
        setHistory(historyData);
      } catch (err) {
        setError("Failed to load clinical note history.");
        console.error(err);
      } finally {
        hideLoading();
      }
    };

    if (open && memberId) {
      void run();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, memberId]);

  const TABLE_HEADERS = [
    {
      label: "Id",
      width: "4%",
      minWidth: "40px",
      maxWidth: "56px",
      align: "center",
    },
    { label: "Assessment Type", width: "18%" },
    { label: "Program(s)", width: "17%" },
    { label: "Program Outcome", width: "18%" },
    { label: "Start Date", width: "10%" },
    { label: "Completion Date", width: "10%" },
    { label: "Time Spent", width: "8%" },
    { label: "Submitted By", width: "8%" },
    { label: "", width: "6%" }, // action column
  ];

  // Per-row merge plan so rows sharing the same start & completion date have
  // their Assessment Type / Start Date / Completion Date / Time Spent /
  // Submitted By columns shown in a single vertically-merged cell.
  const mergePlan = buildMergePlan(history);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      slotProps={{
        paper: {
          sx: {
            width: {
              xs: "95vw",
              sm: "90vw",
              md: "90vw",
              lg: "80vw",
              xl: "70vw",
            },
            borderRadius: "16px",
            backgroundColor: "#F7F8FB",
            maxHeight: "90vh",
            m: 1,
          },
        },
      }}
    >
      <Box sx={{ position: "relative", p: 2 }}>
        {/* ── Header ── */}
        <DialogTitle
          sx={{
            p: 0,
            m: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: "20px", color: "#000000" }}
          >
            Clinical Assessment History
          </Typography>
          <IconButton
            onClick={onClose}
            sx={{ color: "#403C6F" }}
            size="small"
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        {/* ── Content ── */}
        <DialogContent
          sx={{
            p: 0,
            backgroundColor: "#FFFFFF",
            borderRadius: "12px",
            border: "1px solid #E0E0E0",
            overflowX: "hidden",
            minHeight: "400px",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {error ? (
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                flex: 1,
                color: "error.main",
                py: 6,
              }}
            >
              {error}
            </Box>
          ) : (
            <TableContainer
              sx={{
                maxHeight: "60vh",
                p: 2,
                pt: 0,
                overflowY: "auto",
                overflowX: "auto",
                position: "relative",
                mt: 2,
                width: "100%",
              }}
            >
              <Table
                stickyHeader
                size="small"
                sx={{ minWidth: "800px", tableLayout: "auto" }}
              >
                {/* ── Table Head ── */}
                <TableHead>
                  <TableRow
                    sx={{
                      position: "sticky",
                      top: 0,
                      zIndex: 5000,
                      backgroundColor: "#CFCEDA",
                      boxShadow: "0 2px 0 rgba(0,0,0,0.05)",
                    }}
                  >
                    {TABLE_HEADERS.map((header) => (
                      <TableCell
                        key={header.label || "action"}
                        align={
                          header.align ||
                          (header.label === "Id" ? "center" : "left")
                        }
                        sx={{
                          backgroundColor: "#CFCEDA",
                          color: "#403C6F",
                          borderColor: "#f5f5f8ff",
                          borderWidth: "0px 1px 0px 1px ",
                          borderStyle: "solid",
                          fontWeight: 700,
                          fontSize: "14px",
                          py: 1.5,
                          px: header.label === "Id" ? 1 : 2,
                          borderBottom: "none",
                          whiteSpace: "nowrap",
                          width: header.width,
                          minWidth: header.minWidth || undefined,
                          maxWidth: header.maxWidth || undefined,
                          position: "sticky",
                          top: 0,
                          zIndex: 5000,
                          ...(header.label === "Id" && {
                            width: "48px",
                            minWidth: "40px",
                            maxWidth: "56px",
                            textAlign: "center",
                          }),
                        }}
                      >
                        {header.label}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>

                {/* ── Table Body ── */}
                <TableBody>
                  {mergePlan.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={TABLE_HEADERS.length}
                        align="center"
                        sx={{
                          py: 10,
                          color: "#66638C",
                          fontSize: "14px",
                          borderBottom: "none",
                        }}
                      >
                        No clinical assessment history available.
                      </TableCell>
                    </TableRow>
                  ) : (
                    mergePlan.map((entry, index) => {
                      const item = entry.item;
                      const isDraft = isDraftItem(item);
                      const batchKey = item.batchId || item.id;
                      const outcomeStyle = getOutcomeStyle(
                        item.outcome,
                        item.status,
                        null,
                        item,
                      );

                      /* Programs: mapped to display name like in opportunities */
                      const programs = getProgramDisplayName(item);
                      const hasPrograms = hasProgramList(item);

                      return (
                        <TableRow
                          key={item.id || index}
                          sx={{
                            "&:hover": { backgroundColor: "#F7F8FB" },
                            "& td": { borderBottom: "1px solid #E0E0E0" },
                          }}
                        >
                          {/* Id — groups programs in a single assessment */}
                          {entry.emitMerged && (
                            <TableCell
                              rowSpan={entry.rowSpan}
                              align="center"
                              sx={{
                                color: "#403C6F",
                                fontSize: "14px",
                                fontWeight: 600,
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 1px 0px 1px",
                                borderStyle: "solid",
                                backgroundColor: "#FFFFFF",
                                verticalAlign: "middle",
                                width: "48px",
                                minWidth: "40px",
                                maxWidth: "56px",
                                px: 1,
                              }}
                            >
                              {entry.id ?? entry.slNo}
                            </TableCell>
                          )}

                          {/* Assessment Type — displayed against each program */}
                          <TableCell
                            sx={{
                              color: "#403C6F",
                              fontSize: "14px",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              borderColor: "#E8E8E9",
                              borderWidth: "0px 1px 0px 1px ",
                              borderStyle: "solid",
                            }}
                          >
                            {getAssessmentTypeLabel(item)}
                          </TableCell>

                          {/* Program(s) */}
                          <TableCell
                            sx={{
                              borderColor: "#E8E8E9",
                              borderWidth: "0px 0.5px 0px 0.5px ",
                              borderStyle: "solid",
                              padding: "8px 16px",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1,
                              }}
                            >
                              {hasPrograms ? (
                                item.programs.map((prog, idx) => {
                                  const progId = prog.programId || prog;
                                  const displayName = formatProgramName(progId);
                                  return (
                                    <Box
                                      key={prog.programId || idx}
                                      sx={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        border: "1px solid #ECECF1",
                                        backgroundColor: "#F7F8FB",
                                        borderRadius: "40px",
                                        px: 1.5,
                                        py: 0.5,
                                        fontWeight: 500,
                                        width: "fit-content",
                                        height: "28px",
                                        whiteSpace: "nowrap",
                                      }}
                                    >
                                      {getCategoryIcon(displayName)}
                                      <span
                                        style={{
                                          marginLeft: 6,
                                          color: "#403C6F",
                                          fontWeight: 700,
                                          fontSize: "12px",
                                        }}
                                      >
                                        {displayName}
                                      </span>
                                    </Box>
                                  );
                                })
                              ) : (
                                <Box
                                  sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    border: "1px solid #ECECF1",
                                    backgroundColor: "#F7F8FB",
                                    borderRadius: "40px",
                                    px: 1.5,
                                    py: 0.5,
                                    fontWeight: 500,
                                    width: "fit-content",
                                    height: "28px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {getCategoryIcon(programs)}
                                  <span
                                    style={{
                                      marginLeft: 6,
                                      color: "#403C6F",
                                      fontWeight: 700,
                                      fontSize: "12px",
                                    }}
                                  >
                                    {programs}
                                  </span>
                                </Box>
                              )}
                            </Box>
                          </TableCell>

                          {/* Outcome */}
                          <TableCell
                            sx={{
                              borderColor: "#E8E8E9",
                              borderWidth: "0px 0.5px 0px 0.5px ",
                              borderStyle: "solid",
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.5,
                              }}
                            >
                              {hasPrograms ? (
                                item.programs.map((prog, idx) => {
                                  const progOutcomeStyle = getOutcomeStyle(
                                    prog.outcome || item.outcome,
                                    item.status,
                                    prog,
                                    item,
                                  );
                                  const subReason =
                                    prog.subReason ||
                                    prog.outcomeReason ||
                                    item.subReason ||
                                    item.outcomeReason;
                                  return (
                                    <Box
                                      key={prog.programId || idx}
                                      sx={{
                                        display: "flex",
                                        flexDirection: "column",
                                        justifyContent: "center",
                                      }}
                                    >
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: "13px",
                                          color:
                                            progOutcomeStyle.color || "#6c757d",
                                          display: "flex",
                                          alignItems: "center",
                                        }}
                                      >
                                        {progOutcomeStyle.label}
                                      </Typography>
                                      {subReason && (
                                        <Typography
                                          sx={{
                                            fontWeight: 500,
                                            fontSize: "11px",
                                            color: "#7F7C9F",
                                            mt: 0.25,
                                          }}
                                        >
                                          ({capitalizeFirstLetter(subReason)})
                                        </Typography>
                                      )}
                                    </Box>
                                  );
                                })
                              ) : (
                                <Box
                                  sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    justifyContent: "center",
                                  }}
                                >
                                  <Typography
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: "13px",
                                      color: outcomeStyle.color || "#6c757d",
                                    }}
                                  >
                                    {outcomeStyle.label}
                                  </Typography>
                                  {(item.subReason || item.outcomeReason) && (
                                    <Typography
                                      sx={{
                                        fontWeight: 500,
                                        fontSize: "11px",
                                        color: "#7F7C9F",
                                        mt: 0.25,
                                      }}
                                    >
                                      (
                                      {capitalizeFirstLetter(
                                        item.subReason || item.outcomeReason,
                                      )}
                                      )
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          </TableCell>

                          {/* Start Date — merged cell for duplicate-date rows */}
                          {entry.emitMerged ? (
                            <TableCell
                              rowSpan={entry.rowSpan}
                              sx={{
                                color: "#403C6F",
                                fontSize: "14px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              {renderDateTime(item.startTime)}
                            </TableCell>
                          ) : null}

                          {/* Completion Date — merged cell for duplicate-date rows */}
                          {entry.emitMerged ? (
                            <TableCell
                              rowSpan={entry.rowSpan}
                              sx={{
                                color: "#403C6F",
                                fontSize: "14px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              {renderDateTime(item.completionTime)}
                            </TableCell>
                          ) : null}

                          {/* Time Spent — merged cell for duplicate-date rows */}
                          {entry.emitMerged ? (
                            <TableCell
                              rowSpan={entry.rowSpan}
                              sx={{
                                color: "#403C6F",
                                fontSize: "14px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              {getTimeSpent(entry, item)}
                            </TableCell>
                          ) : null}

                          {/* Submitted By — merged cell for duplicate-date rows */}
                          {entry.emitMerged ? (
                            <TableCell
                              rowSpan={entry.rowSpan}
                              sx={{
                                color: "#403C6F",
                                fontSize: "14px",
                                fontWeight: 600,
                                whiteSpace: "nowrap",
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              {getSubmittedBy(entry, item)}
                            </TableCell>
                          ) : null}

                          {/* Action column — single merged preview per batch */}
                          {entry.emitMerged ? (
                            <TableCell
                              rowSpan={entry.rowSpan}
                              align="center"
                              sx={{
                                borderColor: "#E8E8E9",
                                borderWidth: "0px 0.5px 0px 0.5px ",
                                borderStyle: "solid",
                              }}
                            >
                              <Box
                                sx={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  gap: 0.5,
                                }}
                              >
                                {isDraft ? (
                                  /* Draft → Reopen button */
                                  <Tooltip
                                    title="Edit draft"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        onClose();
                                        if (onReopen)
                                          onReopen({
                                            ...item,
                                            id: batchKey,
                                            isBatch: true,
                                          });
                                      }}
                                      sx={{
                                        color: "#403C6F",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(64,60,111,0.08)",
                                        },
                                      }}
                                      aria-label="Edit note"
                                    >
                                      <EditOutlinedIcon
                                        sx={{ fontSize: "18px" }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                ) : (
                                  /* Submitted → Single View (read-only) */
                                  <Tooltip
                                    title="View note (read-only)"
                                    arrow
                                    placement="top"
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => {
                                        onClose();
                                        if (onReopen)
                                          onReopen(
                                            {
                                              ...item,
                                              id: batchKey,
                                              isBatch: true,
                                            },
                                            true,
                                          );
                                      }}
                                      sx={{
                                        color: "#403C6F",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(64,60,111,0.08)",
                                        },
                                      }}
                                      aria-label="View note"
                                    >
                                      <VisibilityOutlinedIcon
                                        sx={{ fontSize: "18px" }}
                                      />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                <Tooltip
                                  title="Download PDF"
                                  arrow
                                  placement="top"
                                >
                                  <span>
                                    <IconButton
                                      size="small"
                                      disabled={downloadingPdfId === batchKey}
                                      onClick={() => handleDownloadPdf(item)}
                                      sx={{
                                        color: "#403C6F",
                                        "&:hover": {
                                          backgroundColor:
                                            "rgba(64,60,111,0.08)",
                                        },
                                      }}
                                      aria-label="Download PDF"
                                    >
                                      {downloadingPdfId === batchKey ? (
                                        <CircularProgress
                                          size={16}
                                          sx={{ color: "#403C6F" }}
                                        />
                                      ) : (
                                        <DownloadOutlinedIcon
                                          sx={{ fontSize: "18px" }}
                                        />
                                      )}
                                    </IconButton>
                                  </span>
                                </Tooltip>
                              </Box>
                            </TableCell>
                          ) : null}
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Box>
    </Dialog>
  );
};

ClinicalHistoryModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  memberId: PropTypes.string,
  memberData: PropTypes.object,
  onReopen: PropTypes.func,
};

export default ClinicalHistoryModal;
